import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import Reveal from "@/components/Reveal";
import Portrait from "@/components/Portrait";

function LeaderCard({ leader, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <Link
        to={`/leadership/${leader.slug}`}
        data-testid={`leader-card-${leader.slug}`}
        className="card-premium group flex h-full flex-col items-start"
      >
        <Portrait name={leader.fullName} src={leader.photograph} size="md" testid={`portrait-${leader.slug}`} />
        <h3 className="mt-6 font-serif text-2xl text-navy-900 transition-colors group-hover:text-gold-dark">
          {leader.fullName}
        </h3>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">{leader.designation}</p>
        <p className="mt-1 text-xs text-slate-500">{leader.brand || leader.organisation}</p>
        {leader.shortBiography && (
          <p className="mt-4 text-sm leading-relaxed text-slate-600 line-clamp-3">{leader.shortBiography}</p>
        )}
        <span className="link-underline mt-auto pt-6 text-sm">
          View Profile <ArrowRight className="h-4 w-4" aria-hidden />
        </span>
      </Link>
    </Reveal>
  );
}

export default function Leadership() {
  const [leaders, setLeaders] = useState([]);
  const [brands, setBrands] = useState([]);
  const [tab, setTab] = useState("");

  useEffect(() => {
    api.get("/leadership").then((r) => setLeaders(r.data)).catch(() => {});
    api.get("/brands").then((r) => {
      setBrands(r.data);
      if (r.data.length) setTab(r.data[0].brandName);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const corporate = leaders.filter((l) => l.leadershipCategory === "corporate" || !l.brand);
  const brandLeaders = leaders.filter((l) => l.brand && l.brand.toLowerCase() === tab.toLowerCase());
  const founder = corporate.find((l) => /founder|chairman|managing director/i.test(l.designation || ""));

  return (
    <div data-testid="leadership-page">
      <PageHero
        eyebrow="Leadership"
        title="The people shaping our vision and building our businesses."
        lede="RCS leadership combines corporate governance, educational expertise and entrepreneurial ambition — across the parent company and each of its brands."
      />

      {/* FOUNDER */}
      {founder && (
        <section className="border-b border-navy-900/10 bg-white py-24 lg:py-32" data-testid="founder-section">
          <div className="container-rcs">
            <Section eyebrow="Meet Our Founder" title="A founder-led education organisation." />
            <Reveal delay={0.15}>
              <div className="mt-12 grid items-start gap-12 rounded-2xl border border-navy-900/10 bg-ivory p-8 lg:grid-cols-12 lg:p-14">
                <div className="lg:col-span-4">
                  <Portrait name={founder.fullName} src={founder.photograph} size="xl" testid="founder-portrait" />
                </div>
                <div className="lg:col-span-8">
                  <h3 className="font-serif text-3xl text-navy-900 sm:text-4xl">{founder.fullName}</h3>
                  <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">{founder.designation}</p>
                  <p className="mt-1 text-sm text-slate-500">{founder.organisation}</p>
                  {founder.shortBiography ? (
                    <p className="mt-6 text-base leading-relaxed text-slate-600">{founder.shortBiography}</p>
                  ) : (
                    <p className="mt-6 text-base leading-relaxed text-slate-600">
                      Leads corporate strategy, business development, educational ventures, technology,
                      organisational growth, expansion and strategic planning for Roy Career Solutions.
                    </p>
                  )}
                  {founder.quote && (
                    <blockquote className="mt-8 border-l-2 border-gold pl-6 font-serif text-xl italic text-navy-900">
                      “{founder.quote}”
                    </blockquote>
                  )}
                  <Link to={`/leadership/${founder.slug}`} className="btn-gold mt-10" data-testid="founder-profile-cta">
                    View Full Profile <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* CORPORATE LEADERSHIP */}
      <section className="py-24 lg:py-32" data-testid="corporate-leadership">
        <div className="container-rcs">
          <Section eyebrow="Board & Corporate Leadership" title="Corporate leadership at RCS." />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {corporate.map((l, i) => (
              <LeaderCard key={l.id} leader={l} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* BRAND LEADERSHIP */}
      <section className="bg-navy-950 py-24 lg:py-32" data-testid="brand-leadership-section">
        <div className="container-rcs">
          <Section
            dark
            eyebrow="Leadership Across Our Brands"
            title="Specialised leaders for specialised brands."
            lede="Each RCS brand is led by dedicated executives across academics, operations, technology and growth."
          />
          <div className="mt-12 flex flex-wrap gap-3" role="tablist" aria-label="Brand leadership tabs">
            {brands.map((b) => (
              <button
                key={b.id}
                role="tab"
                aria-selected={tab === b.brandName}
                data-testid={`tab-${b.slug}`}
                onClick={() => setTab(b.brandName)}
                className={`rounded-full px-6 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  tab === b.brandName
                    ? "bg-gold text-navy-950"
                    : "border border-white/20 text-slate-300 hover:border-gold hover:text-gold"
                }`}
              >
                {b.brandName}
              </button>
            ))}
          </div>
          <div className="mt-12">
            {brandLeaders.length > 0 ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {brandLeaders.map((l, i) => (
                  <Reveal key={l.id} delay={i * 0.1}>
                    <Link
                      to={`/leadership/${l.slug}`}
                      data-testid={`brand-leader-card-${l.slug}`}
                      className="group flex h-full flex-col items-start rounded-2xl border border-white/10 bg-navy-900/60 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                    >
                      <Portrait name={l.fullName} src={l.photograph} size="md" />
                      <h3 className="mt-6 font-serif text-2xl text-ivory transition-colors group-hover:text-gold">{l.fullName}</h3>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold">{l.designation}</p>
                      {l.shortBiography && (
                        <p className="mt-4 text-sm leading-relaxed text-slate-400 line-clamp-3">{l.shortBiography}</p>
                      )}
                      <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                        View Profile <ArrowRight className="h-4 w-4" aria-hidden />
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            ) : (
              <Reveal>
                <p className="max-w-xl rounded-2xl border border-dashed border-white/20 bg-navy-900/40 px-8 py-10 text-sm leading-relaxed text-slate-400" data-testid="brand-leadership-empty">
                  Leadership profiles for {tab} will be published here as they are announced.
                </p>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
